import {MainSeekbar} from '@app/web-player/player-controls/seekbar/main-seekbar';
import {useIsMobileMediaQuery} from '@ui/utils/hooks/is-mobile-media-query';
import {useMediaQuery} from '@ui/utils/hooks/use-media-query';
import clsx from 'clsx';
import {PlayButton} from '@common/player/ui/controls/play-button';
import {NextButton} from '@common/player/ui/controls/next-button';
import {PreviousButton} from '@common/player/ui/controls/previous-button';
import {ShuffleButton} from '@common/player/ui/controls/shuffle-button';
import {RepeatButton} from '@common/player/ui/controls/repeat-button';
import {BufferingIndicator} from '@app/web-player/player-controls/buffering-indicator';

interface Props {
  className?: string;
  layout?: 'default' | 'fullscreen' | 'overlay';
}

export function PlaybackControls({className, layout = 'default'}: Props) {
  if (layout === 'fullscreen') {
    return (
      <div className={className}>
        <MainSeekbar variant="fullscreen" />
        <FullscreenTransportControls />
      </div>
    );
  }

  if (layout === 'overlay') {
    return (
      <div className={className}>
        <OverlayTransportControls />
        <MainSeekbar variant="overlay" className="mt-16" />
      </div>
    );
  }

  return (
    <div className={className}>
      <PlaybackButtons />
      <MainSeekbar />
    </div>
  );
}

function FullscreenTransportControls() {
  const isTouch = !!useMediaQuery('(max-width: 1024px)');

  return (
    <div
      className={clsx(
        'mt-16 flex items-center justify-center md:mt-24',
        isTouch ? 'gap-14 md:gap-20' : 'gap-24',
      )}
    >
      <TransportButtonRow variant="fullscreen" isTouch={isTouch} />
    </div>
  );
}

function OverlayTransportControls() {
  const isTouch = !!useMediaQuery('(max-width: 1024px)');

  return (
    <div
      className={clsx(
        'flex items-center justify-center',
        isTouch ? 'gap-10 md:gap-14' : 'gap-6',
      )}
    >
      <TransportButtonRow variant="overlay" isTouch={isTouch} />
    </div>
  );
}

function TransportButtonRow({
  variant,
  isTouch,
}: {
  variant: 'fullscreen' | 'overlay';
  isTouch: boolean;
}) {
  const isFullscreen = variant === 'fullscreen';
  const btnColor = isFullscreen ? 'white' : undefined;
  const btnClass = isFullscreen ? 'text-white' : undefined;

  return (
    <>
      <ShuffleButton
        size={isTouch ? 'md' : isFullscreen ? 'lg' : 'md'}
        color={btnColor}
        iconSize={isTouch ? 'md' : isFullscreen ? 'lg' : 'sm'}
        className={btnClass}
      />
      <PreviousButton
        size={isTouch ? 'md' : isFullscreen ? 'lg' : 'md'}
        color={btnColor}
        iconSize={isTouch ? 'lg' : isFullscreen ? 'lg' : 'md'}
        className={btnClass}
      />
      <div className="relative">
        <BufferingIndicator className={btnClass} />
        <PlayButton
          size={isTouch ? 'lg' : isFullscreen ? 'xl' : 'md'}
          color={btnColor}
          iconSize="xl"
          className={isFullscreen ? 'shadow-lg' : undefined}
        />
      </div>
      <NextButton
        size={isTouch ? 'md' : isFullscreen ? 'lg' : 'md'}
        color={btnColor}
        iconSize={isTouch ? 'lg' : isFullscreen ? 'lg' : 'md'}
        className={btnClass}
      />
      <RepeatButton
        size={isTouch ? 'md' : isFullscreen ? 'lg' : 'md'}
        color={btnColor}
        iconSize={isTouch ? 'md' : isFullscreen ? 'lg' : 'sm'}
        className={btnClass}
      />
    </>
  );
}

function PlaybackButtons() {
  const isMobile = useIsMobileMediaQuery();
  const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1024px)');

  return (
    <div
      className={clsx(
        'flex items-center justify-center',
        isMobile ? 'mb-16 gap-8' : isTablet ? 'mb-14 gap-10' : 'mb-12 gap-6',
      )}
    >
      <ShuffleButton iconSize={isMobile || isTablet ? 'md' : 'sm'} />
      <PreviousButton
        size="md"
        iconSize={isMobile || isTablet ? 'lg' : 'md'}
      />
      <div className="relative">
        <BufferingIndicator />
        <PlayButton size={isMobile || isTablet ? 'lg' : 'md'} iconSize="xl" />
      </div>
      <NextButton size="md" iconSize={isMobile || isTablet ? 'lg' : 'md'} />
      <RepeatButton iconSize={isMobile || isTablet ? 'md' : 'sm'} />
    </div>
  );
}
